import * as React from "react";
import { Link } from "react-router-dom";
import {
  Activity,
  AlertCircle,
  ArrowLeft,
  Bell,
  BellOff,
  History,
  Loader2,
  MoreHorizontal,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  ShieldQuestion,
  Trash2,
  X,
} from "lucide-react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  Checkbox,
  DataTable,
  DataTableEmpty,
  DataTableFooter,
  DataTableScroll,
  DataTableSkeleton,
  DataTableToolbar,
  DataTableToolbarSpacer,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  PageHeader,
  PageHeaderActions,
  PageHeaderDescription,
  PageHeaderHeading,
  PageHeaderTitle,
  PageHeaderTop,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  cn,
  toast,
} from "@eduviaos/ui-kit";
import { ApiError, userHasPermission, type NotificationTrigger } from "@eduviaos/shared";
import { NotificationTriggerEditorSheet } from "@/components/NotificationTriggerEditorSheet";
import { NotificationLogsSheet } from "@/components/NotificationLogsSheet";
import {
  useArchiveNotificationTrigger,
  useNotificationTriggers,
  useToggleNotificationTrigger,
} from "@/hooks/useNotificationTriggers";
import { useAuth } from "@/auth/AuthProvider";

/**
 * Phase F0b — Notification triggers landing page (IA Settings →
 * Notification triggers).
 *
 * Iron Rule #16 — sticky-header DataTable, hover-only row highlight,
 * action column last + ⋯ dropdown trigger, no zebra stripes.
 *
 * Defense-in-depth: BE enforces `manage_notifications` per route; this
 * page additionally hides write affordances from users who lack the
 * permission so they don't fill out the editor and hit a 403 on save.
 *
 * The page also exposes a "Show archived" toggle so the IA can see
 * soft-deleted triggers (and, in a follow-up release, restore them).
 */

const QUERY_KEY = "notification-triggers-list";

export function NotificationTriggersPage({
  surface = "settings",
}: {
  surface?: "settings" | "module";
} = {}) {
  const [qInput, setQInput] = React.useState("");
  const [includeArchived, setIncludeArchived] = React.useState(false);
  const [editorOpen, setEditorOpen] = React.useState(false);
  const [editingTrigger, setEditingTrigger] = React.useState<NotificationTrigger | null>(null);
  const [logsOpen, setLogsOpen] = React.useState(false);
  const [logsTrigger, setLogsTrigger] = React.useState<NotificationTrigger | null>(null);
  const [pendingDelete, setPendingDelete] = React.useState<NotificationTrigger | null>(null);
  const [deleteError, setDeleteError] = React.useState<string | null>(null);

  const { data, isLoading, isFetching, refetch, isError, error } = useNotificationTriggers({
    includeArchived,
  });
  const archiveMutation = useArchiveNotificationTrigger();
  const toggleMutation = useToggleNotificationTrigger();

  const { user } = useAuth();
  const canWrite = React.useMemo(
    () =>
      user
        ? userHasPermission(
            user.role,
            user.permission_overrides ?? [],
            "manage_notifications",
            user.permission_mode ?? "additive",
          )
        : false,
    [user],
  );

  const items = React.useMemo<NotificationTrigger[]>(() => {
    const all = data?.items ?? [];
    const trimmed = qInput.trim().toLowerCase();
    if (!trimmed) return all;
    return all.filter(
      (t) =>
        t.name.toLowerCase().includes(trimmed) ||
        t.event.toLowerCase().includes(trimmed) ||
        t.template_slug.toLowerCase().includes(trimmed) ||
        (t.description ?? "").toLowerCase().includes(trimmed),
    );
  }, [data?.items, qInput]);

  const total = data?.items.length ?? 0;

  const openCreate = () => {
    setEditingTrigger(null);
    setEditorOpen(true);
  };

  const openEdit = (t: NotificationTrigger) => {
    setEditingTrigger(t);
    setEditorOpen(true);
  };

  const openLogs = (t: NotificationTrigger) => {
    setLogsTrigger(t);
    setLogsOpen(true);
  };

  const handleSaved = (saved: NotificationTrigger, action: "create" | "update") => {
    toast.success(
      action === "update" ? `Saved changes to "${saved.name}".` : `Created "${saved.name}".`,
    );
    setEditingTrigger(null);
  };

  const handleToggle = async (t: NotificationTrigger) => {
    try {
      const next = await toggleMutation.mutateAsync(t.id);
      toast.success(
        next.enabled
          ? `Enabled "${next.name}". The dispatcher will fire on the next matching event.`
          : `Disabled "${next.name}". Existing events will not dispatch until you flip it back on.`,
      );
    } catch (raw) {
      toast.error(raw instanceof Error && raw.message ? raw.message : "Could not toggle.");
    }
  };

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleteError(null);
    try {
      await archiveMutation.mutateAsync(pendingDelete.id);
      toast.success(`Archived "${pendingDelete.name}".`);
      setPendingDelete(null);
    } catch (raw) {
      if (raw instanceof ApiError && raw.code === "CONFLICT") {
        setDeleteError(
          "This trigger has open dependencies and cannot be archived right now. Try again in a moment.",
        );
      } else if (raw instanceof Error && raw.message) {
        setDeleteError(raw.message);
      } else {
        setDeleteError("Could not archive — please try again.");
      }
    }
  };

  return (
    <div className="mx-auto max-w-6xl py-8" data-testid={QUERY_KEY}>
      {surface === "settings" ? (
        <Button variant="ghost" size="sm" asChild className="mb-4">
          <Link to="/settings">
            <ArrowLeft aria-hidden="true" className="size-4" />
            Back to Settings
          </Link>
        </Button>
      ) : null}

      <PageHeader>
        <PageHeaderTop>
          <PageHeaderTitle>
            <PageHeaderHeading>
              {surface === "module" ? "Communications" : "Notification triggers"}
            </PageHeaderHeading>
            <PageHeaderDescription>
              {surface === "module"
                ? "Email and SMS templates tied to platform events — enquiries, admissions, and learner lifecycle."
                : 'Map platform events (e.g. an enquiry advancing to "application submitted") to a template + recipients. Triggers fire asynchronously — config changes take effect within seconds.'}
            </PageHeaderDescription>
          </PageHeaderTitle>
          <PageHeaderActions>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void refetch()}
              disabled={isFetching}
              aria-label="Refresh triggers"
            >
              <RefreshCw
                aria-hidden="true"
                className={cn("size-4", isFetching && "animate-spin")}
              />
              Refresh
            </Button>
            {canWrite ? (
              <Button size="sm" onClick={openCreate} data-testid="triggers-create">
                <Plus aria-hidden="true" className="size-4" />
                New trigger
              </Button>
            ) : null}
          </PageHeaderActions>
        </PageHeaderTop>
      </PageHeader>

      {!canWrite && user ? (
        <div
          role="status"
          className="border-border/60 bg-muted/40 text-muted-foreground mt-4 flex items-start gap-2 rounded-md border px-3 py-2 text-xs"
          data-testid="triggers-readonly-banner"
        >
          <ShieldQuestion
            aria-hidden="true"
            className="text-muted-foreground mt-0.5 size-3.5 shrink-0"
          />
          <span className="flex-1">
            You're viewing this surface in read-only mode. Authoring or archiving notification
            triggers requires the <code className="text-foreground">manage_notifications</code>{" "}
            permission. Ask the institution admin or another admin to grant it on the Members tab.
          </span>
        </div>
      ) : null}

      {isError ? (
        <div
          role="alert"
          className="border-destructive/30 bg-destructive/5 text-destructive mt-4 flex items-start gap-2 rounded-md border px-3 py-2 text-xs"
        >
          <AlertCircle aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
          <span className="flex-1">
            Couldn't load notification triggers
            {error instanceof Error && error.message ? `: ${error.message}` : "."}
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="h-auto px-2 py-0.5 text-xs"
            onClick={() => void refetch()}
          >
            Retry
          </Button>
        </div>
      ) : null}

      <DataTable className="mt-6">
        <DataTableToolbar>
          <div className="relative w-full sm:w-72">
            <Search
              aria-hidden="true"
              className="text-muted-foreground absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2"
            />
            <Input
              type="search"
              value={qInput}
              onChange={(e) => setQInput(e.target.value)}
              placeholder="Filter by name, event, or template…"
              className="pl-8"
              aria-label="Filter triggers"
              data-testid="triggers-filter"
            />
          </div>
          {qInput ? (
            <Button variant="ghost" size="sm" onClick={() => setQInput("")}>
              <X aria-hidden="true" className="size-3.5" />
              Clear
            </Button>
          ) : null}
          <label className="text-muted-foreground inline-flex cursor-pointer items-center gap-1.5 text-xs">
            <Checkbox
              checked={includeArchived}
              onCheckedChange={(c) => setIncludeArchived(Boolean(c))}
              data-testid="toggle-archived"
            />
            Show archived
          </label>
          <DataTableToolbarSpacer />
          <span className="text-muted-foreground text-xs tabular-nums">
            {total.toLocaleString()} total
          </span>
        </DataTableToolbar>

        <DataTableScroll>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[26%]">Name</TableHead>
                <TableHead className="w-[22%]">Event</TableHead>
                <TableHead className="w-[14%]">Channels</TableHead>
                <TableHead className="w-[10%] text-right">Recipients</TableHead>
                <TableHead className="w-[18%]">Last activity</TableHead>
                <TableHead className="w-[10%] text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            {isLoading ? (
              <DataTableSkeleton rows={5} columns={6} />
            ) : (
              <TableBody>
                {items.map((t) => (
                  <TriggerRow
                    key={t.id}
                    trigger={t}
                    canWrite={canWrite}
                    onEdit={openEdit}
                    onLogs={openLogs}
                    onToggle={() => void handleToggle(t)}
                    isToggling={toggleMutation.isPending && toggleMutation.variables === t.id}
                    onArchive={(target) => {
                      setDeleteError(null);
                      setPendingDelete(target);
                    }}
                  />
                ))}
              </TableBody>
            )}
          </Table>
        </DataTableScroll>

        {!isLoading && items.length === 0 ? (
          <DataTableEmpty
            icon={<Bell aria-hidden="true" />}
            title={
              isError
                ? "Couldn't load notification triggers"
                : qInput
                  ? "No matches"
                  : "No triggers yet"
            }
            description={
              isError
                ? "Refresh to retry — if it keeps failing, check the network."
                : qInput
                  ? "Try a different filter or clear it to see the full list."
                  : "Create a trigger to fire emails or in-app notifications when admission events happen."
            }
            action={
              isError ? (
                <Button variant="outline" size="sm" onClick={() => void refetch()}>
                  <RefreshCw aria-hidden="true" className="size-4" />
                  Retry
                </Button>
              ) : qInput ? (
                <Button variant="outline" size="sm" onClick={() => setQInput("")}>
                  <X aria-hidden="true" className="size-3.5" />
                  Clear filter
                </Button>
              ) : canWrite ? (
                <Button size="sm" onClick={openCreate}>
                  <Plus aria-hidden="true" className="size-4" />
                  New trigger
                </Button>
              ) : null
            }
          />
        ) : null}

        {!isLoading && total > 0 ? (
          <DataTableFooter>
            <span className="text-muted-foreground text-sm tabular-nums">
              Showing {items.length.toLocaleString()} of {total.toLocaleString()}
              {qInput ? " (filtered)" : ""}
              {includeArchived ? " (archived included)" : ""}
            </span>
          </DataTableFooter>
        ) : null}
      </DataTable>

      <Card className="bg-muted/40 mt-4">
        <CardContent className="text-muted-foreground py-3 text-xs">
          <strong className="text-foreground">How it works:</strong> the dispatcher loads all
          enabled triggers for an event and fans out to their recipients in parallel. Failed
          deliveries retry up to three times with exponential backoff. Soft-disabled triggers stay
          configured but skip dispatch — flip the toggle in the row's actions menu to re-arm them.
        </CardContent>
      </Card>

      <NotificationTriggerEditorSheet
        open={editorOpen}
        onOpenChange={(open) => {
          setEditorOpen(open);
          if (!open) setEditingTrigger(null);
        }}
        existing={editingTrigger}
        onSaved={handleSaved}
      />

      <NotificationLogsSheet
        open={logsOpen}
        onOpenChange={(open) => {
          setLogsOpen(open);
          if (!open) setLogsTrigger(null);
        }}
        trigger={logsTrigger}
      />

      <Dialog
        open={Boolean(pendingDelete)}
        onOpenChange={(open) => {
          if (!open) {
            setPendingDelete(null);
            setDeleteError(null);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Archive {pendingDelete?.name}?</DialogTitle>
            <DialogDescription>
              The trigger stops firing immediately and is hidden from the active list. Existing
              audit logs are preserved for forensics — toggle "Show archived" to restore later (in a
              follow-up release).
            </DialogDescription>
          </DialogHeader>
          {deleteError ? (
            <div className="bg-destructive/10 text-destructive rounded-md px-3 py-2 text-sm">
              {deleteError}
            </div>
          ) : null}
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" disabled={archiveMutation.isPending}>
                Cancel
              </Button>
            </DialogClose>
            <Button
              variant="destructive"
              onClick={() => void handleConfirmDelete()}
              disabled={archiveMutation.isPending}
            >
              {archiveMutation.isPending ? (
                <>
                  <Loader2 aria-hidden="true" className="size-4 animate-spin" />
                  Archiving…
                </>
              ) : (
                "Archive trigger"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

interface TriggerRowProps {
  trigger: NotificationTrigger;
  canWrite: boolean;
  onEdit: (t: NotificationTrigger) => void;
  onLogs: (t: NotificationTrigger) => void;
  onToggle: () => void;
  isToggling: boolean;
  onArchive: (t: NotificationTrigger) => void;
}

function TriggerRow({
  trigger,
  canWrite,
  onEdit,
  onLogs,
  onToggle,
  isToggling,
  onArchive,
}: TriggerRowProps) {
  const lastActivity = trigger.last_failed_at ?? trigger.last_dispatched_at;
  const lastFailedNewer = Boolean(
    trigger.last_failed_at &&
    (!trigger.last_dispatched_at ||
      new Date(trigger.last_failed_at) >= new Date(trigger.last_dispatched_at)),
  );

  return (
    <TableRow data-testid={`trigger-row-${trigger.id}`}>
      <TableCell>
        <div className="flex items-start gap-2">
          {trigger.enabled ? (
            <Bell aria-hidden="true" className="text-muted-foreground mt-0.5 size-3.5 shrink-0" />
          ) : (
            <BellOff
              aria-hidden="true"
              className="text-muted-foreground/60 mt-0.5 size-3.5 shrink-0"
            />
          )}
          <div className="min-w-0">
            <div className="text-foreground font-medium">{trigger.name}</div>
            {trigger.description ? (
              <div className="text-muted-foreground mt-0.5 line-clamp-1 text-xs">
                {trigger.description}
              </div>
            ) : null}
            {!trigger.enabled ? (
              <Badge variant="outline" className="mt-1 text-[10px] font-normal">
                Disabled
              </Badge>
            ) : null}
          </div>
        </div>
      </TableCell>
      <TableCell className="text-xs">
        <code className="bg-muted text-foreground rounded px-1.5 py-0.5 font-mono text-[10.5px]">
          {trigger.event}
        </code>
        {!trigger.event_is_known ? (
          <div
            className="mt-1 inline-flex items-center gap-1 text-[10.5px] text-amber-700 dark:text-amber-300"
            title="Event not in catalogue"
          >
            <Activity aria-hidden="true" className="size-3" />
            Unknown event
          </div>
        ) : null}
      </TableCell>
      <TableCell className="text-xs">
        <div className="flex flex-wrap gap-1">
          {trigger.channels.map((c) => (
            <Badge key={c} variant="outline" className="text-[10.5px] font-normal capitalize">
              {c.replace(/_/g, " ")}
            </Badge>
          ))}
        </div>
      </TableCell>
      <TableCell className="text-right text-xs tabular-nums">
        {trigger.recipients.length.toLocaleString()}
      </TableCell>
      <TableCell className="text-xs">
        {lastActivity ? (
          <div className={cn("flex items-start gap-1.5", lastFailedNewer && "text-destructive")}>
            <Activity aria-hidden="true" className="mt-0.5 size-3 shrink-0" />
            <div className="min-w-0">
              <div className="tabular-nums">{formatRelative(lastActivity)}</div>
              <div className="text-muted-foreground text-[10.5px]">
                {lastFailedNewer ? "Last attempt failed" : "Last dispatch ok"}
              </div>
            </div>
          </div>
        ) : (
          <span className="text-muted-foreground/60">No fires yet</span>
        )}
      </TableCell>
      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
        {canWrite ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                aria-label={`Actions for ${trigger.name}`}
                data-testid={`trigger-actions-${trigger.id}`}
              >
                <MoreHorizontal aria-hidden="true" className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[200px]">
              <DropdownMenuItem onSelect={() => onEdit(trigger)}>
                <Pencil aria-hidden="true" className="size-3.5" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => onLogs(trigger)}>
                <History aria-hidden="true" className="size-3.5" />
                View dispatch history
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={onToggle} disabled={isToggling}>
                {trigger.enabled ? (
                  <BellOff aria-hidden="true" className="size-3.5" />
                ) : (
                  <Bell aria-hidden="true" className="size-3.5" />
                )}
                {trigger.enabled ? "Disable" : "Enable"}
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => onArchive(trigger)}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 aria-hidden="true" className="size-3.5" />
                Archive
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onLogs(trigger)}
            aria-label={`View history for ${trigger.name}`}
          >
            <History aria-hidden="true" className="size-3.5" />
            History
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
}

function formatRelative(iso: string): string {
  try {
    const ts = new Date(iso).getTime();
    const now = Date.now();
    const diffMs = now - ts;
    if (diffMs < 0) return "just now";
    const seconds = Math.floor(diffMs / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 14) return `${days}d ago`;
    return new Date(iso).toLocaleDateString();
  } catch {
    return iso;
  }
}
